import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import axios from "axios";
import SendingForm from "./SendingForm";
import CryptoJS from "crypto-js";

const secretKey = process.env.REACT_APP_SECRET_KEY;

if (!secretKey) {
    console.error("Error: Secret key is undefined! Check your .env file.");
}

function encryptData(data) {
    if (!data) {
        console.error("Encryption Error: No data provided.");
        return null;
    }
    if (!secretKey) {
        console.error("Encryption Error: Secret key is undefined!");
        return null;
    }
    return CryptoJS.AES.encrypt(data.toString(), secretKey).toString();
}

function Login() {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [attempts, setAttempts] = useState(parseInt(localStorage.getItem("loginAttempts")) || 0);
    const [isBlocked, setIsBlocked] = useState(false);
    const [firstName, setFirstName] = useState(sessionStorage.getItem("first_name") || "");
    const [lastName, setLastName] = useState(sessionStorage.getItem("last_name") || "");

    useEffect(() => {
        const storedLogin = sessionStorage.getItem("isLoggedIn");
        if (storedLogin) setIsLoggedIn(true);

        const blockTime = localStorage.getItem("blockTime");
        if (blockTime) {
            const timeLeft = 300000 - (Date.now() - parseInt(blockTime));
            if (timeLeft > 0) {
                setIsBlocked(true);
                setTimeout(() => {
                    setIsBlocked(false);
                    localStorage.removeItem("blockTime");
                    setAttempts(0);
                    localStorage.setItem("loginAttempts", "0");
                }, timeLeft);
            } else {
                localStorage.removeItem("blockTime");
                setAttempts(0);
                localStorage.setItem("loginAttempts", "0");
            }
        }
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();

        if (isBlocked) {
            Swal.fire({
                icon: "error",
                title: "Too many attempts",
                text: "Please wait 5 minutes before trying again."
            });
            return;
        }

        const encryptedUsername = encryptData(username);
        const encryptedPassword = encryptData(password);

        if (!encryptedUsername || !encryptedPassword) {
            console.error("Encryption failed!");
            return;
        }

        const api_host = window.location.hostname.indexOf('localhost') >= 0 ? "http://localhost:4000" : "";
        try {
            const response = await axios.post(api_host + "/api/login", {
                username: encryptedUsername,
                password: encryptedPassword
            });

            if (response.data.success) {
                setIsLoggedIn(true);
                setAttempts(0);
                sessionStorage.setItem("isLoggedIn", "true");
                sessionStorage.setItem("tbl_id", response.data.tbl_id);
                sessionStorage.setItem("first_name", response.data.first_name);
                sessionStorage.setItem("last_name", response.data.last_name);
                setFirstName(response.data.first_name);
                setLastName(response.data.last_name);
            } else {
                const newAttempts = attempts + 1;
                setAttempts(newAttempts);
                localStorage.setItem("loginAttempts", newAttempts.toString());

                if (newAttempts >= 3) {
                    setIsBlocked(true);
                    localStorage.setItem("blockTime", Date.now().toString());
                    Swal.fire({
                        icon: "error",
                        title: "Too many failed attempts",
                        text: "You have been blocked for 5 minutes."
                    });
                } else {
                    Swal.fire({
                        icon: "error",
                        title: "Invalid Credentials",
                        text: response.data.message || "Please check your username and password."
                    });
                }
            }
        } catch (error) {
            console.error("Login error:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "An error occurred while trying to log in. Please try again later."
            });
        }
    };

    const handleLogout = () => {
        setIsLoggedIn(false);
        sessionStorage.removeItem("isLoggedIn");
        sessionStorage.removeItem("tbl_id");
        sessionStorage.removeItem("first_name");
        sessionStorage.removeItem("last_name");
        setFirstName("");
        setLastName("");
    };

    if (isLoggedIn) {
        return (
            <div>
                <header className="d-flex justify-content-between align-items-center p-3 bg-light shadow">
                    <h2>Welcome, {firstName} {lastName}!</h2>
                    <button onClick={handleLogout} className="btn btn-danger">Logout</button>
                </header>
                <SendingForm onLogout={handleLogout} />
            </div>
        );
    }

    return (
        <div className="login-container d-flex justify-content-center align-items-center vh-100">
            <div className="card p-4 shadow" style={{ maxWidth: "400px", width: "100%" }}>
                <div className="text-center mb-4">
                    <img src="https://amaphil.com.ph/wp-content/uploads/2024/12/amaphil-tap.png" alt="AMAPHIL Logo" className="img-fluid" style={{ maxWidth: "300px" }} />
                </div>
                <form onSubmit={handleLogin}>
                    <div className="mb-3">
                        <label htmlFor="username" className="form-label">Username:</label>
                        <input
                            type="text"
                            id="username"
                            className="form-control"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                            disabled={isBlocked}
                        />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="password" className="form-label">Password:</label>
                        <input
                            type="password"
                            id="password"
                            className="form-control"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            disabled={isBlocked}
                        />
                    </div>
                    <button type="submit" className="btn btn-primary w-100" disabled={isBlocked}>Login</button>
                </form>
            </div>
        </div>
    );
}

function App() {
    return (
        <Login />
    );
}

export default App;
